import { Target, FileText, Activity, ArrowRight } from 'lucide-react';
import { useLocation } from 'wouter';

import type { BetsOverviewProps } from '@shared/widget-schemas';
import { cn } from '@/lib/utils';
import { useShortIdFromLocation } from '@/hooks/v2/useShortIdFromLocation';
import { ChatCard, ConfidencePill, CountPill, TrendArrow, mix } from './chat-widget-ui';
import { capitalizeFirst } from './textFormat';
import { SkBar, SkChip, SkCircle } from './skeletons';

const PURPLE = 'var(--color-purple-400)';
const BLUE = 'var(--color-blue-400)';

const ROW_DIVIDER = '1px solid color-mix(in srgb, var(--border) 50%, transparent)';

export function BetsOverviewWidget({ title = 'Top bets', bets }: BetsOverviewProps) {
  const totalEvidence = bets.reduce((s, b) => s + b.evidence, 0);
  const totalSignals = bets.reduce((s, b) => s + b.signals, 0);
  const [, setLocation] = useLocation();
  const shortId = useShortIdFromLocation();
  const goToResearch = () => {
    if (shortId) setLocation(`/projects/${shortId}/research`);
  };

  return (
    <ChatCard className="overflow-hidden text-sm">
      <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
        <span
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-lg"
          style={{ background: mix(PURPLE, 14), border: `1px solid ${mix(PURPLE, 28)}`, color: PURPLE }}
        >
          <Target className="size-[14px]" aria-hidden />
        </span>
        <span className="text-[15px] font-bold text-foreground">{capitalizeFirst(title)}</span>
        <div className="ml-auto flex gap-3.5 text-[11px] text-muted-foreground">
          <span>
            <strong className="font-mono text-foreground">{totalEvidence}</strong> evidence
          </span>
          <span>
            <strong className="font-mono text-foreground">{totalSignals}</strong> signals
          </span>
        </div>
      </div>

      {bets.map((bet, i) => (
        <div
          key={bet.id}
          role="listitem"
          className="flex items-center gap-4 px-4 py-[11px]"
          style={i < bets.length - 1 ? { borderBottom: ROW_DIVIDER } : undefined}
        >
          <ConfidencePill lvl={bet.lvl} className="shrink-0">
            {bet.confidence}%
          </ConfidencePill>
          <span className="min-w-0 max-w-[54%] flex-1 text-[12.5px] font-medium leading-[1.35] text-foreground">
            {capitalizeFirst(bet.title)}
          </span>
          <div className="ml-auto flex shrink-0 items-center gap-2.5">
            <TrendArrow dir={bet.trend} />
            <div className="flex gap-1.5">
              <CountPill icon={FileText} color={PURPLE}>
                {bet.evidence}
              </CountPill>
              <CountPill icon={Activity} color={BLUE}>
                {bet.signals}
              </CountPill>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={goToResearch}
        data-nav-to="research"
        className="flex w-full items-center justify-center gap-1.5 border-t border-border px-4 py-3 text-[12px] font-medium text-muted-foreground transition-colors hover:bg-[var(--row-hover)] hover:text-foreground"
      >
        View all bets <ArrowRight className="size-[13px]" aria-hidden />
      </button>
    </ChatCard>
  );
}

export function BetsOverviewSkeleton({ className, rows = 3 }: { className?: string; rows?: number }) {
  return (
    <ChatCard className={cn('overflow-hidden', className)}>
      <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
        <SkCircle size="28px" radius="8px" />
        <SkBar width="100px" height="12px" />
        <span className="ml-auto flex gap-3">
          <SkBar width="60px" height="8px" />
          <SkBar width="56px" height="8px" />
        </span>
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 px-4 py-[13px]"
          style={i < rows - 1 ? { borderBottom: ROW_DIVIDER } : undefined}
        >
          <SkChip width="40px" height="22px" />
          <SkBar width={`${48 - i * 4}%`} height="9px" />
          <span className="ml-auto flex items-center gap-2.5">
            <SkBar width="16px" height="11px" />
            <SkChip width="34px" height="18px" />
            <SkChip width="34px" height="18px" />
          </span>
        </div>
      ))}
      <div className="flex items-center justify-center border-t border-border px-4 py-3">
        <SkBar width="96px" height="12px" />
      </div>
    </ChatCard>
  );
}
